/**
 * src/pages/respond/VendorResponse.js — Sourcing Hub quote form (/respond/:id).
 *
 * Opened by vendors from the WhatsApp "Submit quote" button or the inquiry
 * e-mail. Both links carry ?v=<token>, so the quote lands on that vendor's
 * line, and the company name and e-mail come from our vendor list, locked, so
 * nobody can answer as someone else. Contact name and phone are optional.
 * An older link without a token still works: the form then asks for the
 * company and an e-mail / phone, which the server matches. No login.
 *
 *   First visit   the brief and the form, plus PDFs / images to upload.
 *   After send    "What you sent" read-only (SentResponse.js), "Edit my
 *                 response" to reopen the form filled in, and the 1:1 chat
 *                 with Marqland (VendorChat.js).
 *   Closed        a closed or deleted inquiry shows a short closed page with
 *                 info@marqland.com, and nothing else.
 *
 *   Products  one card per product (photo, name, quantity, description) with
 *             the vendor's answer — available / not available / we can supply /
 *             we cannot supply / not our product — and, when they can, a price
 *             per unit, a delivery date (Airbnb-style picker) and notes.
 *   Offsite   a short hello from Marqland Studios, the brief, and the travel
 *             agent's / venue's property options (see OffsiteOffer.js).
 *             Prices are per item only; we never ask for a package total.
 *   Older     inquiries with neither keep the single price / date form.
 *
 * From 1024px wide the brief sits in a sticky left column and the answers on
 * the right, so the page uses the screen instead of one narrow column.
 *
 * The server's quote links (services/sourcing/dispatch.js) point here, at
 * CLIENT_URL/respond/:id, so vendors never touch the admin domain.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Check, CheckCircle2, FileText, Lock, Mail, Pencil, Send, Upload } from 'lucide-react';
import { AIR, AIR_CSS } from '../../styles/airTheme';
import AirDatePicker, { formatDay } from '../../components/AirDatePicker';
import OffsiteOffer, { AV, PROPERTY_TYPES, ROOMS, amount, emptyOption, offerPayload, offerProblem, optionFromSent } from './OffsiteOffer';
import SentResponse, { FileList } from './SentResponse';
import VendorChat from './VendorChat';

const API_BASE = process.env.REACT_APP_API_URL || '';
const serif = AIR.serif;

const ANSWERS = [
  ['available', 'Available'], ['can_supply', 'We can supply'],
  ['not_available', 'Not available'], ['cannot_supply', 'We cannot supply'], ['not_our_product', 'Not our product'],
];
const POSITIVE = ['available', 'can_supply'];
const AV_LABEL = Object.fromEntries(AV);
const PROPERTY_LABEL = Object.fromEntries(PROPERTY_TYPES);
const SECTIONS = [
  ['conference', 'Conference facilities & meeting spaces'],
  ['galaDinner', 'Gala dinner & bar services'],
  ['activities', 'On-site activities'],
  ['branding', 'Branding & setup'],
  ['transport', 'Transportation & parking'],
];

const CONTACT_EMAIL = 'info@marqland.com';
const MAX_FILES = 10;
const BLANK_LINE = { status: '', unitPrice: '', deliveryDate: '', notes: '' };

const isImage = (f) => (f?.fileType || '').startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(f?.url || '');
const stay = (o) => (o?.checkIn || o?.checkOut ? `${formatDay(o.checkIn) || '?'} – ${formatDay(o.checkOut) || '?'}` : '');

const card = { background: '#fff', border: `1px solid ${AIR.line2}`, borderRadius: 12, padding: '22px 22px 18px', marginBottom: 16 };
const lbl = { display: 'block', fontFamily: AIR.sans, fontWeight: 600, fontSize: 13, color: AIR.ink, margin: '0 0 6px' };
const sub = { fontSize: 14, color: AIR.muted, lineHeight: 1.6 };
const h2 = { fontFamily: serif, fontWeight: 400, fontSize: 24, color: AIR.ink, margin: '0 0 14px' };

// Two columns from 1024px: the brief stays in view on the left while the
// vendor fills in the answers on the right.
const LAYOUT_CSS = `
  .vr-grid { display: block; }
  .vr-two { display: grid; gap: 20px; }
  @media (min-width: 1024px) {
    .vr-grid { display: grid; grid-template-columns: minmax(360px, 440px) minmax(0, 1fr); gap: 28px; align-items: start; }
    .vr-aside { position: sticky; top: 24px; max-height: calc(100vh - 48px); overflow-y: auto; scrollbar-width: thin; }
    .vr-two { grid-template-columns: minmax(0, 1fr) 360px; gap: 24px; align-items: start; }
    .vr-chat { position: sticky; top: 24px; }
  }
  .vr-banner { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; border-radius: 12px; padding: 14px 18px; margin-bottom: 20px; font-size: 14px; line-height: 1.5; }
  .vr-drop { border: 1.5px dashed ${AIR.field}; border-radius: 12px; padding: 22px 16px; text-align: center; background: #fff; cursor: pointer; }
  .vr-drop.over { border-color: ${AIR.ink}; background: ${AIR.studio}; }
  .vr-lock { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 48px; padding: 12px 14px; border: 1px solid ${AIR.line2}; border-radius: 8px; background: ${AIR.studio}; font-size: 15px; color: ${AIR.ink}; overflow: hidden; }
`;

const Shell = ({ children, subtitle = 'Request for quote', narrow }) => (
  <div className="air air-forms" style={{ minHeight: '100vh', background: AIR.studio }}>
    <style>{AIR_CSS}{LAYOUT_CSS}</style>
    <header style={{ background: '#fff', borderBottom: `1px solid ${AIR.line}`, padding: '12px 20px' }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/logo.svg" alt="Marqland Studios logo" width={40} height={40} style={{ borderRadius: '50%', boxShadow: `0 0 0 1px ${AIR.gold}` }} />
          <div style={{ fontFamily: serif, color: AIR.ink, fontWeight: 500, fontSize: 26, lineHeight: 1.1 }}>Marqland <em style={{ color: AIR.goldText }}>Studios.</em></div>
        </div>
        <div style={{ color: AIR.muted, fontWeight: 500, fontSize: 13 }}>{subtitle}</div>
      </div>
    </header>
    <main style={{ maxWidth: narrow ? 720 : 1240, margin: '0 auto', padding: '24px 16px 48px' }}>{children}</main>
  </div>
);

const Chip = ({ on, onClick, children, label }) => (
  <button type="button" className="air-chip" aria-pressed={on} aria-label={label} onClick={onClick} style={{ minHeight: 40 }}>
    {on && <Check size={14} />}{children}
  </button>
);

const Rows = ({ rows }) => (
  <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, 150px) minmax(0, 1fr)', rowGap: 10, columnGap: 16, fontSize: 14 }}>
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <dt style={{ color: AIR.muted, fontWeight: 600, fontSize: 13 }}>{k}</dt>
        <dd style={{ margin: 0, color: AIR.ink, whiteSpace: 'pre-line', lineHeight: 1.55 }}>{v}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const offsiteRows = (o) => [
  ['Destinations', (o.destinations || []).join(' · ')],
  ['Property type', (o.propertyTypes || []).map((t) => PROPERTY_LABEL[t] || t).join(', ')],
  ['Check-in / check-out', stay(o)],
  ['Event agenda', o.agenda],
  ['No. of pax', o.pax],
  ['Rooms', ROOMS.filter(([k]) => o.rooms?.[k]).map(([k, l]) => `${o.rooms[k]} ${l.toLowerCase()}`).join(', ')],
  ['Inclusions', [...(o.mealPlans || []), o.inclusions].filter(Boolean).join(' · ')],
  ...SECTIONS.map(([k, l]) => [l, o[k]]),
  ['AV requirements', (o.av || []).map((a) => AV_LABEL[a] || a).join(', ')],
].filter(([, v]) => v);

/** One product: the brief and the vendor's answer for it. */
const ProductCard = ({ n, item, line, onChange, deadline }) => {
  const set = (patch) => onChange({ ...line, ...patch });
  const positive = POSITIVE.includes(line.status);
  return (
    <section style={card}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
        {item.image?.url && (
          <a href={item.image.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>
            <img src={item.image.url} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 12, border: `1px solid ${AIR.line}`, display: 'block' }} />
          </a>
        )}
        <div style={{ minWidth: 0 }}>
          <p style={{ ...lbl, color: AIR.muted, marginBottom: 2 }}>Product {n}</p>
          <h3 style={{ fontFamily: serif, fontWeight: 400, fontSize: 24, color: AIR.ink, margin: '0 0 4px', lineHeight: 1.15 }}>{item.name}</h3>
          {item.quantity && <div style={{ fontSize: 14, fontWeight: 600 }}>Quantity: {item.quantity}</div>}
          {item.description && <p style={{ ...sub, margin: '6px 0 0', whiteSpace: 'pre-line', color: AIR.ink }}>{item.description}</p>}
        </div>
      </div>

      <span style={lbl}>Your answer *</span>
      <div role="group" aria-label={`Your answer for ${item.name}`} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: positive ? 16 : 12 }}>
        {ANSWERS.map(([k, label]) => <Chip key={k} on={line.status === k} onClick={() => set({ status: k })}>{label}</Chip>)}
      </div>

      {positive && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={lbl} htmlFor={`p-${item._id}`}>Price per unit (₹) *</label>
            <input id={`p-${item._id}`} type="text" required inputMode="decimal" value={line.unitPrice} onChange={(e) => set({ unitPrice: amount(e.target.value) })} placeholder="e.g. 450" />
          </div>
          <div>
            <span style={lbl}>Can deliver by *</span>
            <AirDatePicker label="Delivery date" value={line.deliveryDate} onChange={(deliveryDate) => set({ deliveryDate })} placeholder={deadline ? `Needed by ${deadline}` : 'Add date'} />
          </div>
        </div>
      )}
      {line.status && (
        <div>
          <label style={lbl} htmlFor={`n-${item._id}`}>Notes</label>
          <input id={`n-${item._id}`} type="text" value={line.notes} onChange={(e) => set({ notes: e.target.value })} maxLength={1000}
            placeholder={positive ? 'MOQ, branding, GST, alternatives…' : 'Optional — e.g. an alternative you can offer'} />
        </div>
      )}
    </section>
  );
};


/** A closed or deleted inquiry: no brief, no form, no prices. */
const Closed = () => (
  <Shell narrow subtitle="Corporate offsites, MICE & gifting">
    <section style={{ ...card, textAlign: 'center', padding: '56px 24px 52px', marginTop: 24 }}>
      <img src="/logo.svg" alt="" width={88} height={88} style={{ borderRadius: '50%', boxShadow: `0 0 0 1px ${AIR.gold}`, display: 'block', margin: '0 auto 20px' }} />
      <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: 36, color: AIR.ink, margin: '0 0 12px' }}>This inquiry is closed</h1>
      <p style={{ ...sub, margin: '0 0 4px', fontSize: 15 }}>Thank you for your time. This request is no longer open for quotes.</p>
      <p style={{ ...sub, margin: '0 0 22px', fontSize: 15 }}>For future orders, please contact Marqland Studios.</p>
      <a href={`mailto:${CONTACT_EMAIL}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: `1px solid ${AIR.gold}`, borderRadius: 40, padding: '10px 20px', color: AIR.ink, fontWeight: 600, textDecoration: 'none', fontSize: 15 }}>
        <Mail size={16} /> {CONTACT_EMAIL}
      </a>
    </section>
  </Shell>
);

const stamp = (at) => {
  if (!at) return '';
  const d = new Date(at);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/** Form state from what the vendor sent (or blank on a first visit). */
const fromSent = (data, r) => ({
  lines: Object.fromEntries((data.items || []).map((i) => {
    const l = (r?.lines || []).find((x) => String(x.itemId) === String(i._id));
    return [i._id, l ? { status: l.status || '', unitPrice: l.unitPrice ? String(l.unitPrice) : '', deliveryDate: l.deliveryDate || '', notes: l.notes || '' } : { ...BLANK_LINE }];
  })),
  offer: r?.offsite
    ? { available: r.offsite.available || '', properties: r.offsite.properties?.length ? r.offsite.properties.map((p) => optionFromSent(p, data.offsite)) : [emptyOption(data.offsite)] }
    : { available: '', properties: data.offsite ? [emptyOption(data.offsite)] : [] },
  files: r?.files || [],
  contactName: r?.contactName || data.vendor?.contactName || '',
  phone: r?.phone || data.vendor?.phone || '',
  notes: r?.notes || '',
});

export default function VendorResponse() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('v') || '';
  const [inquiry, setInquiry] = useState(null);
  const [closed, setClosed] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ canSupply: 'yes', unitPrice: '', deliveryDate: '', notes: '', companyName: '', contact: '', contactName: '', phone: '' });
  const [lines, setLines] = useState({});
  const [offer, setOffer] = useState({ available: '', properties: [] });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [over, setOver] = useState(false);
  const [response, setResponse] = useState(null);
  const [chat, setChat] = useState([]);
  const [editing, setEditing] = useState(false);
  const [banner, setBanner] = useState('');
  const [anonSent, setAnonSent] = useState(false);
  const picker = useRef(null);

  const fill = useCallback((data, r) => {
    const f = fromSent(data, r);
    setLines(f.lines);
    setOffer(f.offer);
    setFiles(f.files);
    setForm((x) => ({ ...x, contactName: f.contactName, phone: f.phone, notes: f.notes }));
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/api/inquiries/public/${id}`, { params: token ? { v: token } : {} })
      .then((r) => {
        setInquiry(r.data);
        setResponse(r.data.response || null);
        setChat(r.data.chat || []);
        fill(r.data, r.data.response);
      })
      .catch((e) => {
        // Closed and deleted inquiries look the same to the vendor.
        if ([403, 404, 400].includes(e.response?.status)) setClosed(true);
        else setLoadError('This page could not be loaded. Please check your connection and try again.');
      });
  }, [id, token, fill]);

  // New messages from Marqland appear without a reload.
  const knownVendor = !!inquiry?.vendor;
  useEffect(() => {
    if (!knownVendor) return undefined;
    const t = setInterval(() => {
      axios.get(`${API_BASE}/api/inquiries/public/${id}`, { params: { v: token } })
        .then((r) => setChat(r.data.chat || []))
        .catch((e) => { if ([403, 404].includes(e.response?.status)) setClosed(true); });
    }, 30000);
    return () => clearInterval(t);
  }, [knownVendor, id, token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === 'unitPrice' ? amount(e.target.value) : e.target.value }));
  const items = useMemo(() => inquiry?.items || [], [inquiry]);
  const isOffsite = !!inquiry?.offsite;
  const legacy = inquiry && !items.length && !isOffsite;
  const viewing = knownVendor && response && !editing;
  const sentUrls = useMemo(() => new Set((response?.files || []).map((f) => f.url)), [response]);

  const upload = async (list) => {
    const picked = Array.from(list || []).slice(0, MAX_FILES - files.length);
    if (!picked.length) { if (list?.length) setError(`Up to ${MAX_FILES} files.`); return; }
    const bad = picked.find((f) => !/^image\//.test(f.type) && f.type !== 'application/pdf');
    if (bad) { setError(`“${bad.name}” isn't a PDF or an image.`); return; }
    const data = new FormData();
    picked.forEach((f) => data.append('files', f));
    setUploading(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/inquiries/public/${id}/files`, data, { params: { v: token } });
      setFiles((x) => [...x, ...(r.data.files || [])].slice(0, MAX_FILES));
    } catch (ex) {
      setError(ex.response?.data?.message || 'The files could not be uploaded. Please try again.');
    } finally {
      setUploading(false);
      if (picker.current) picker.current.value = '';
    }
  };

  const problem = () => {
    if (items.length) {
      const missing = items.find((i) => !lines[i._id]?.status);
      if (missing) return `Choose an answer for “${missing.name}”.`;
      const incomplete = items.find((i) => POSITIVE.includes(lines[i._id].status) && (!lines[i._id].unitPrice || !lines[i._id].deliveryDate));
      if (incomplete) return `Add a price and a delivery date for “${incomplete.name}”.`;
    }
    if (isOffsite && offerProblem(offer)) return offerProblem(offer);
    if (legacy && form.canSupply === 'yes' && (!form.unitPrice || !form.deliveryDate)) return 'Add your price per unit and the date you can deliver by.';
    if (!knownVendor && (!form.companyName.trim() || !form.contact.trim())) return 'Add your company name and the e-mail or phone we contacted you on.';
    if (uploading) return 'Wait for the files to finish uploading.';
    return '';
  };

  const submit = async (e) => {
    e?.preventDefault();
    const p = problem();
    if (p) { setError(p); return; }
    setError('');
    setSending(true);
    const edit = !!response;
    try {
      const r = await axios.post(`${API_BASE}/api/inquiries/public/${id}/respond`, {
        token: token || null,
        ...(legacy ? {
          canSupply: form.canSupply,
          unitPrice: form.canSupply === 'yes' ? form.unitPrice : '',
          deliveryDate: form.canSupply === 'yes' ? form.deliveryDate : '',
        } : {}),
        ...(items.length ? { lines: items.map((i) => ({ itemId: i._id, ...lines[i._id] })) } : {}),
        ...(isOffsite ? { offsite: offerPayload(offer) } : {}),
        notes: form.notes,
        files: knownVendor ? files : [],
        companyName: knownVendor ? inquiry.vendor.companyName : form.companyName,
        contact: knownVendor ? inquiry.vendor.email : form.contact,
        contactName: form.contactName,
        phone: form.phone,
      });
      if (knownVendor && r.data.response) {
        setResponse(r.data.response);
        setChat(r.data.chat || []);
        setEditing(false);
        setBanner(edit ? 'saved' : 'sent');
        window.scrollTo?.({ top: 0 });
      } else {
        setAnonSent(true);
      }
    } catch (ex) {
      if (ex.response?.status === 403 && !ex.response?.data?.message?.includes('link')) setClosed(true);
      else setError(ex.response?.data?.message || 'Your response could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const cancelEdit = () => { fill(inquiry, response); setEditing(false); setError(''); window.scrollTo?.({ top: 0 }); };

  if (closed) return <Closed />;
  if (loadError) {
    return <Shell narrow><div style={{ textAlign: 'center', padding: '60px 0' }}><AlertCircle size={40} color={AIR.danger} /><h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: 28, color: AIR.ink }}>{loadError}</h1></div></Shell>;
  }
  if (!inquiry) return <Shell narrow><div style={{ textAlign: 'center', padding: 60, color: AIR.muted }}>Loading…</div></Shell>;
  const subtitle = isOffsite ? 'Corporate offsites & MICE · Request for quote' : 'Request for quote';
  const titleText = inquiry.title || (items.length ? `${items.length} product${items.length === 1 ? '' : 's'}` : 'Product requirement');
  if (anonSent) {
    return (
      <Shell narrow subtitle={subtitle}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <CheckCircle2 size={48} color={AIR.success} />
          <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: 32, color: AIR.ink, margin: '16px 0 8px' }}>Thank you.</h1>
          <p style={{ color: AIR.muted, fontSize: 15 }}>Your response for {inquiry.refNumber} has reached Marqland Studios.</p>
        </div>
      </Shell>
    );
  }

  const facts = [
    !items.length && !isOffsite && ['Quantity', inquiry.quantity],
    !isOffsite && ['Required by', inquiry.deadline],
    ['Target price', inquiry.targetPrice],
  ].filter((x) => x && x[1]);

  const briefCard = (
    <section style={{ ...card, marginBottom: 0 }}>
      <p style={{ ...lbl, color: AIR.muted }}>{inquiry.refNumber}{knownVendor ? ` · for ${inquiry.vendor.companyName}` : ''}</p>
      <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: 30, color: AIR.ink, margin: '0 0 12px', lineHeight: 1.15 }}>{titleText}</h1>
      {inquiry.publicDescription && <p style={{ fontSize: 14, color: AIR.ink, lineHeight: 1.7, whiteSpace: 'pre-line', margin: '0 0 16px' }}>{inquiry.publicDescription}</p>}
      {isOffsite && <div style={{ marginBottom: facts.length ? 16 : 0 }}><Rows rows={offsiteRows(inquiry.offsite)} /></div>}
      {items.length > 0 && (viewing || editing) && (
        <ul style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>
          {items.map((i) => <li key={i._id}>{i.name}{i.quantity ? ` × ${i.quantity}` : ''}</li>)}
        </ul>
      )}
      {facts.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', background: AIR.studio, borderRadius: 12, overflow: 'hidden' }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ padding: '12px 14px' }}><p style={{ ...lbl, color: AIR.muted, marginBottom: 2 }}>{k}</p><div style={{ fontSize: 15 }}>{v}</div></div>
          ))}
        </div>
      )}
      {inquiry.attachments?.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          {inquiry.attachments.map((f, i) => (
            <a key={i} href={f.url} target="_blank" rel="noreferrer" style={{ display: 'block', border: `1px solid ${AIR.line}`, borderRadius: 8, overflow: 'hidden', textDecoration: 'none' }}>
              {isImage(f)
                ? <img src={f.url} alt={f.name || ''} style={{ width: 120, height: 120, objectFit: 'cover', display: 'block' }} />
                : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 12px', fontSize: 14, color: AIR.ink }}><FileText size={16} /> {f.name || 'Reference file'}</span>}
            </a>
          ))}
        </div>
      )}
    </section>
  );

  const chatPanel = knownVendor ? <div className="vr-chat"><VendorChat inquiryId={id} token={token} chat={chat} setChat={setChat} /></div> : null;

  // ── After send: what they sent, read-only, with the chat beside it ──
  if (viewing) {
    const at = response.updatedAt || response.receivedAt;
    return (
      <Shell subtitle={`${inquiry.refNumber} · ${titleText}`}>
        <div className="vr-banner" style={{ background: AIR.successBg, border: `1px solid #cfe9cf` }}>
          <span>
            {banner === 'saved'
              ? <><b>Changes saved on {stamp(at)}.</b> Marqland Studios has been told.</>
              : <><b>Sent to Marqland Studios on {stamp(response.receivedAt)}.</b> {response.updatedAt ? `Last changed ${stamp(response.updatedAt)}. ` : ''}You can open this link any time to check or change your answers.</>}
          </span>
          <button type="button" className="air-btn" onClick={() => { setEditing(true); setBanner(''); window.scrollTo?.({ top: 0 }); }}><Pencil size={14} /> Edit my response</button>
        </div>
        <div className="vr-two">
          <div style={{ display: 'grid', gap: 16, minWidth: 0 }}>
            <SentResponse response={response} vendor={inquiry.vendor} />
            <details style={{ ...card, marginBottom: 0 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>Our request</summary>
              <div style={{ marginTop: 14 }}>{briefCard}</div>
            </details>
          </div>
          {chatPanel}
        </div>
      </Shell>
    );
  }

  const filesCard = knownVendor && (
    <section style={card}>
      <h2 style={h2}>{isOffsite ? 'Brochures, rate cards & photos' : 'Quotes, catalogues & photos'}</h2>
      <div className={`vr-drop${over ? ' over' : ''}`} role="button" tabIndex={0}
        onClick={() => picker.current?.click()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); picker.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files); }}>
        <Upload size={20} color={AIR.muted} style={{ display: 'block', margin: '0 auto' }} />
        <div style={{ fontSize: 15, marginTop: 6 }}>{uploading ? 'Uploading…' : <><b>Drop PDFs or images here</b> or <u>choose files</u></>}</div>
        <div style={{ fontSize: 13, color: AIR.muted, marginTop: 4 }}>Up to {MAX_FILES} files{files.length ? ` · ${files.length} added` : ''}</div>
        <input ref={picker} type="file" multiple accept="application/pdf,image/*" hidden onChange={(e) => upload(e.target.files)} />
      </div>
      {files.length > 0 && <div style={{ marginTop: 12 }}><FileList files={files} onRemove={(i) => setFiles((x) => x.filter((_, k) => k !== i))} isNew={editing ? (f) => !sentUrls.has(f.url) : null} /></div>}
    </section>
  );

  const aboutCard = (
    <section style={card}>
      {legacy && (
        <>
          <h2 style={h2}>Your quote</h2>
          <div role="group" aria-label="Can you supply?" style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            {[['yes', 'We can supply'], ['no', 'We cannot supply']].map(([k, label]) => (
              <button key={k} type="button" className="air-chip" aria-pressed={form.canSupply === k} onClick={() => setForm((f) => ({ ...f, canSupply: k }))}
                style={{ flex: 1, justifyContent: 'center', minHeight: 48, padding: '0 8px', fontSize: 15 }}>
                {label}
              </button>
            ))}
          </div>
          {form.canSupply === 'yes' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 14 }}>
              <div><label style={lbl} htmlFor="q-pr">Price per unit (₹) *</label><input id="q-pr" type="text" required inputMode="decimal" value={form.unitPrice} onChange={set('unitPrice')} placeholder="e.g. 450" /></div>
              <div><span style={lbl}>Can deliver by *</span><AirDatePicker label="Delivery date" value={form.deliveryDate} onChange={(deliveryDate) => setForm((f) => ({ ...f, deliveryDate }))} /></div>
            </div>
          )}
        </>
      )}

      {!legacy && <h2 style={h2}>About you</h2>}
      {knownVendor ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 10 }}>
            <div><span style={lbl}>Company</span><div className="vr-lock" title="From our vendor list"><span>{inquiry.vendor.companyName}</span><Lock size={14} color={AIR.muted} /></div></div>
            <div><span style={lbl}>E-mail</span><div className="vr-lock" title="From our vendor list"><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{inquiry.vendor.email || '—'}</span><Lock size={14} color={AIR.muted} /></div></div>
            <div><label style={lbl} htmlFor="q-cn">Contact person <span style={{ color: AIR.muted, fontWeight: 400 }}>(optional)</span></label><input id="q-cn" type="text" value={form.contactName} onChange={set('contactName')} maxLength={120} /></div>
            <div><label style={lbl} htmlFor="q-ph">Phone <span style={{ color: AIR.muted, fontWeight: 400 }}>(optional)</span></label><input id="q-ph" type="tel" value={form.phone} onChange={set('phone')} maxLength={40} placeholder="+91" /></div>
          </div>
          <p style={{ fontSize: 13, color: AIR.muted, margin: '0 0 14px' }}>Not {inquiry.vendor.companyName}? This link was sent only to them. Please ask Marqland Studios for your own link.</p>
        </>
      ) : (
        <>
          {!legacy && <p style={{ ...sub, margin: '-6px 0 14px' }}>So we know who sent this quote and how to reach you.</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>
            <div><label style={lbl} htmlFor="q-co">Company name *</label><input id="q-co" type="text" required value={form.companyName} onChange={set('companyName')} /></div>
            <div><label style={lbl} htmlFor="q-ct">E-mail or phone *</label><input id="q-ct" type="text" required value={form.contact} onChange={set('contact')} maxLength={200} placeholder="The one we contacted you on" /></div>
          </div>
        </>
      )}

      <div style={{ marginBottom: 18 }}>
        <label style={lbl} htmlFor="q-nt">{legacy ? 'Notes' : 'Anything else?'}</label>
        <textarea id="q-nt" rows={3} value={form.notes} onChange={set('notes')}
          placeholder={legacy && form.canSupply === 'no' ? 'Optional — e.g. an alternative you can offer' : isOffsite ? 'Anything that applies to all your options' : 'GST, payment terms, validity of the quote…'} />
        {!knownVendor && <div style={{ fontSize: 13, color: AIR.muted, marginTop: 6 }}>Photos or a PDF quote? Reply on WhatsApp or to our e-mail — they reach the same request.</div>}
      </div>

      {error && <div role="alert" style={{ background: AIR.dangerBg, color: AIR.danger, fontSize: 14, padding: '10px 12px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {editing ? (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="air-btn-ghost" onClick={cancelEdit}>Cancel</button>
          <button type="submit" className="air-btn" disabled={sending || uploading}>{sending ? 'Saving…' : 'Save changes'}</button>
        </div>
      ) : (
        <button type="submit" className="air-btn" disabled={sending || uploading} style={{ width: '100%' }}>
          {sending ? 'Sending…' : <>Send response <Send size={14} /></>}
        </button>
      )}
    </section>
  );

  const formBody = (
    <form id="vr-form" onSubmit={submit} noValidate style={{ minWidth: 0 }}>
      {items.length > 0 && (
        <>
          <h2 style={{ ...h2, margin: '0 0 12px' }}>Your quote, product by product</h2>
          {items.map((item, n) => (
            <ProductCard key={item._id} n={n + 1} item={item} deadline={inquiry.deadline || ''}
              line={lines[item._id] || BLANK_LINE}
              onChange={(line) => setLines((l) => ({ ...l, [item._id]: line }))} />
          ))}
        </>
      )}
      {isOffsite && <OffsiteOffer brief={inquiry.offsite} offer={offer} setOffer={setOffer} />}
      {filesCard}
      {aboutCard}
    </form>
  );

  // ── Editing: the same form, filled in, with the chat beside it ──
  if (editing) {
    return (
      <Shell subtitle={`${inquiry.refNumber} · ${titleText}`}>
        <div className="vr-banner" style={{ background: '#f8f1e4', border: `1px solid #ead9b8` }}>
          <span><b>Editing your response.</b> Nothing changes for Marqland until you save.</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="air-btn-ghost" onClick={cancelEdit}>Cancel</button>
            <button type="submit" form="vr-form" className="air-btn" disabled={sending || uploading}>{sending ? 'Saving…' : 'Save changes'}</button>
          </span>
        </div>
        <div className="vr-two">
          <div style={{ minWidth: 0 }}>{formBody}</div>
          {chatPanel}
        </div>
      </Shell>
    );
  }

  // ── First visit ──
  return (
    <Shell subtitle={subtitle}>
      <div className="vr-grid">
        <aside className="vr-aside" style={{ marginBottom: 16 }}>
          {isOffsite && (
            <section style={{ background: AIR.ink, color: '#fff', borderRadius: 12, padding: '20px 22px', marginBottom: 16 }}>
              <p style={{ fontFamily: serif, fontSize: 22, margin: '0 0 6px', lineHeight: 1.2 }}>Hello from Marqland <em style={{ color: '#d9bd85' }}>Studios</em></p>
              <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, color: 'rgba(255,255,255,0.85)' }}>
                We're a corporate offsite and MICE booking company. We're planning this event for one of our clients and would love your best property options and rates.
              </p>
            </section>
          )}
          {briefCard}
          {chat.length > 0 && <div style={{ marginTop: 16 }}>{chatPanel}</div>}
        </aside>
        {formBody}
      </div>
    </Shell>
  );
}
